import { SyncDriver, SyncStatus } from "@/types/v2";

/**
 * Mirror of `SYNC_DRIVER_STALE_THRESHOLD_MS` in `integrations/src/lib/const.ts`,
 * which OWNS this value. It is pinned by backend facts: it must exceed
 * `onProcessSyncJob`'s 540s timeout (so a slow-but-healthy page is never called
 * stuck) and fall below `onScheduledQueueSync`'s 12-minute sweep.
 *
 * Duplicated rather than imported because the frontend cannot import from
 * `integrations/`, and editing that package would drag a manual functions
 * deploy into a frontend-only change. The drift-guard test in
 * `syncDriverStatus.test.ts` keeps the two honest.
 *
 * NEVER make this shorter than the backend's value. A stricter frontend would
 * offer Force Sync while a healthy page is still running — the exact bug
 * WEBV3-65 fixes.
 */
export const SYNC_DRIVER_STALE_THRESHOLD_MS = 10 * 60 * 1000;

/**
 * True while a Cloud Function invocation holds the sync claim RIGHT NOW.
 *
 * Narrow by design. A multi-page full sync runs one page per invocation, so
 * this is true only within a page and false in the gaps between them. Drives
 * the disabled state, and gates `isSyncStale` — see the comment there.
 */
export function hasLiveSyncClaim(
  driver: Pick<SyncDriver, "status"> | undefined
): boolean {
  return driver?.status === SyncStatus.InProgress;
}

/**
 * True while a sync run is underway, INCLUDING the gaps between pages of a
 * multi-page full sync.
 *
 * `setCompleted` runs at the end of every page, so `status` drops to "complete"
 * in those gaps; `fullSyncInProgress` is what survives the whole run.
 *
 * Drives the button LABEL only — never the disabled state, and never staleness.
 */
export function isSyncOngoing(
  driver: Pick<SyncDriver, "status" | "fullSyncInProgress"> | undefined
): boolean {
  if (!driver) return false;
  return hasLiveSyncClaim(driver) || driver.fullSyncInProgress === true;
}

/**
 * True when a live claim has gone untouched for longer than the backend's own
 * "assume it failed" threshold — i.e. when Force Sync's override is warranted.
 *
 * Gated on `hasLiveSyncClaim`, NOT `isSyncOngoing`, and that is load-bearing.
 * `setCompleted` never writes `lastSyncAttemptAt` — only `claim()` does — so
 * the stamp is set when a page claims and then ages through that page's
 * execution AND the inter-page queue wait. Inside an "inprogress" window the
 * age is elapsed execution time, capped by the 540s function timeout, so only
 * a genuinely dead invocation crosses this threshold. Widen the gate and the
 * cap disappears: a healthy page that ran 8.5 minutes then waited 2 minutes for
 * capacity would read as stuck. Do not "simplify" this to isSyncOngoing.
 *
 * A missing stamp counts as stale, matching `syncDriverRepository.claim()`'s
 * `!lastAttempt ||` and `checkShouldSync`'s `new Date(0)` fallback.
 */
export function isSyncStale(
  driver: Pick<SyncDriver, "status"> | undefined,
  lastSyncAttemptAtMs: number | undefined,
  nowMs: number
): boolean {
  if (!hasLiveSyncClaim(driver)) return false;
  if (lastSyncAttemptAtMs === undefined) return true;
  return nowMs - lastSyncAttemptAtMs >= SYNC_DRIVER_STALE_THRESHOLD_MS;
}

/**
 * How long the page keeps showing "Queued…" / "Forcing…" while waiting for a
 * requested sync to be claimed. `onScheduledQueueSync`'s pending top-up
 * re-tokens a waiting request within ~2 minutes, so a request still unclaimed
 * past this is congested rather than imminent — better to release the button
 * than to leave it wedged.
 */
export const SYNC_REQUEST_QUEUED_TIMEOUT_MS = 2 * 60 * 1000;

/** A sync request this admin fired that has not been observed starting yet. */
export type PendingSyncRequest = {
  clickedAtMs: number;
  /**
   * `lastSyncAttemptAt` in epoch ms as it read at click time, or `undefined` if
   * the shop had no sync driver document yet.
   */
  driverStampMs: number | undefined;
};

/**
 * True while this admin's just-fired request is still waiting to be picked up.
 *
 * Compares the driver stamp's VALUE rather than comparing timestamps: the stamp
 * is server-written and the click time is browser-written, so a time comparison
 * would be exposed to client clock skew. A changed stamp means a claim happened
 * and the request is underway.
 */
export function isRequestQueued(
  pending: PendingSyncRequest | null,
  lastSyncAttemptAtMs: number | undefined,
  nowMs: number
): boolean {
  if (!pending) return false;
  if (nowMs - pending.clickedAtMs >= SYNC_REQUEST_QUEUED_TIMEOUT_MS) {
    return false;
  }
  return lastSyncAttemptAtMs === pending.driverStampMs;
}
